import React, { FormEvent } from 'react';
import { Table, THead, TBody, TRow, TH, TD } from 'pureUi/Table';
import { PrimaryButton, SecondaryButton, ButtonBar, ButtonSpacer, LinkButton } from 'pureUi/Buttons';
import TextInput from 'pureUi/TextInput';
import { Pagination } from 'pureUi/Pagination';
import Checkbox from 'pureUi/Checkbox';
import Label from 'pureUi/Label';
import { StandardModal, ModalHeader, ModalContent, ModalFooter } from 'pureUi/Modal';
import DismissableNotification from 'pureUi/DismissableNotification';
import { Heading1, Heading2 } from 'styles';
import { createStructuredSelector } from 'reselect';
import { bindActionCreators, Dispatch, compose } from 'redux';
import { connect } from 'react-redux';
import { IOffersListItem } from 'services/BackendApi/types/OffersListResponse';
import { OffersListStylesWrapper } from './OffersListStylesWrapper';
import { isInternalUser } from 'store/modules/auth';
import { Translation } from 'react-i18next';
import { PureSelect } from 'pureUi/forms/PureSelect';
import * as OffersListSelectors from 'store/modules/offersList/selectors';
import { getBootstrapHotelsSelector } from 'store/modules/bootstrap/selectors';
import { ActionButton, ActionLinkButton } from '../../pureUi/Buttons/index';
import MagnifyingGlassIcon from 'ui/Icons/magnifying-glass.component.svg';
import { SvgIcon } from 'ui/SvgIcon';
import { theme } from '../../../tailwind.config';

import * as OffersListActions from 'store/modules/offersList/actions';

import { getTravelAgentsRequestAction } from '../../store/modules/agents/actions';
import { travelAgentSelectOptionsSelector } from '../../store/modules/agents/selectors';

export class OffersListContainer extends React.Component<IOffersListProps> {
  componentDidMount() {
    this.props.getOffersListRequest();
  }

  handleItemsPerPageChange = (itemsPerPage: number) => {
    this.props.setItemsPerPage(itemsPerPage);
  };

  handleFilterChange = (e: FormEvent<HTMLInputElement>) => {
    this.props.setFilter(e.currentTarget.value);
  };

  handleSort = (sortBy: keyof IOffersListItem) => () => {
    const newSortOrder = sortBy === this.props.sortBy && this.props.sortOrder === 'asc' ? 'desc' : 'asc';
    this.props.setSort(sortBy, newSortOrder);
  };

  handlePageChange = (pageNumber: number) => {
    this.props.setPageNumber(pageNumber - 1);
  };

  getSortOrderForProp = (prop: keyof IOffersListItem) =>
    this.props.sortBy === prop ? this.props.sortOrder : undefined;

  getHeadingText = () => {
    let headingText = 'Offers - Loading...';
    if (!this.props.requestPending && this.props.totalResults > 0) {
      headingText = `Offers - ${this.props.totalResults} ${
        this.props.totalResults === 1 ? 'Result' : 'Results'
      } Found.`;
    }

    if (!this.props.requestPending && this.props.totalResults === 0) {
      headingText = 'Offers - No Results';
    }

    return headingText;
  };

  toggleOfferBulkActionSelection = offerUuid => {
    if (this.props.bulkActionSelectedUuids.includes(offerUuid)) {
      this.props.removeOfferUuidFromBulkActionSelectedUuids(offerUuid);
    } else {
      this.props.addOfferUuidToBulkActionSelectedUuids(offerUuid);
    }
  };

  setAllOffersBulkActionSelection = shouldAdd => {
    if (!this.props.offers) {
      return;
    }

    this.props.offers.forEach(offer => {
      if (shouldAdd) {
        this.props.addOfferUuidToBulkActionSelectedUuids(offer.uuid);
      } else {
        this.props.removeOfferUuidFromBulkActionSelectedUuids(offer.uuid);
      }
    });
  };

  handleDeleteSelectedOffers = () => {
    this.props.toggleIsBulkDeleteConfirmOpen();
  };

  handleConfirmDeleteSelectedOffers = () => {
    this.props.confirmRequestToDeleteOffers();
  };

  render() {
    const allOffersOnPageUuid = this.props.offers ? this.props.offers.map(offer => offer.uuid) : [];

    return (
      <OffersListStylesWrapper>
        <Heading2 className="heading">{this.getHeadingText()}</Heading2>

        <div className="settings">
          <Label text="Filter">
            <TextInput
              className="filterInput"
              value={this.props.filter}
              onChange={this.handleFilterChange}
              placeholder="filter by ID, client or name"
            >
              <SvgIcon
                className="searchIcon pt-6px"
                width="24"
                height="24"
                IconComponent={MagnifyingGlassIcon}
                defaultFill={theme.colors['gold-light']}
              />
            </TextInput>
          </Label>

          <Label text="Hotel">
            <PureSelect
              value={this.props.selectedHotel || ''}
              onChange={e => {
                this.props.setSelectedHotel(e.currentTarget.value);
              }}
            >
              <option value="">All hotels</option>
              {this.props.allHotels.map(hotel => (
                <option key={hotel.uuid} value={hotel.uuid}>
                  {hotel.name}
                </option>
              ))}
            </PureSelect>
          </Label>
        </div>

        {this.props.error && (
          <Translation>
            {t => (
              <div className="notification-wrapper">
                <DismissableNotification
                  type="error"
                  onDismiss={() => {
                    this.props.dismissError();
                  }}
                >
                  <p>
                    <>{t('errors.' + this.props.error)}</>
                  </p>
                </DismissableNotification>
              </div>
            )}
          </Translation>
        )}

        <div className="bulk-actions">
          <ButtonBar>
            <ActionButton
              className="bulk-action-delete"
              action="remove"
              disabled={this.props.bulkActionSelectedUuids.length <= 0}
              onClick={this.handleDeleteSelectedOffers}
            >
              Delete Selected Offers
            </ActionButton>
            <ButtonSpacer />
            <ActionLinkButton className="new-offer-button" action="add" to="/offers/create">
              New Offer
            </ActionLinkButton>
          </ButtonBar>
          {this.props.isBulkDeleteConfirmOpen && (
            <StandardModal onClose={this.props.toggleIsBulkDeleteConfirmOpen}>
              <ModalHeader>
                <Heading1>Delete Offers</Heading1>
              </ModalHeader>
              <ModalContent>
                <p>Are you sure you want to delete {this.props.bulkActionSelectedUuids.length} offers?</p>
                <p>
                  <strong>This action cannot be undone!</strong>
                </p>
              </ModalContent>
              <ModalFooter>
                <ButtonBar>
                  <SecondaryButton onClick={this.props.toggleIsBulkDeleteConfirmOpen}>Cancel</SecondaryButton>
                  <PrimaryButton autoFocus onClick={this.handleConfirmDeleteSelectedOffers}>
                    Delete
                  </PrimaryButton>
                </ButtonBar>
              </ModalFooter>
            </StandardModal>
          )}
        </div>

        {!this.props.requestPending && this.props.totalResults > 0 && (
          <Table className="table">
            <THead>
              <TRow>
                <TH className="bulk-action-column">
                  <Checkbox
                    checked={allOffersOnPageUuid.every(uuid => this.props.bulkActionSelectedUuids.includes(uuid))}
                    onChange={() => {
                      // if every offer on the page is already selected, set them off
                      if (allOffersOnPageUuid.every(uuid => this.props.bulkActionSelectedUuids.includes(uuid))) {
                        this.setAllOffersBulkActionSelection(false);
                      } else {
                        this.setAllOffersBulkActionSelection(true);
                      }
                    }}
                  />
                </TH>
                <TH
                  sortOrder={this.getSortOrderForProp('name')}
                  onClick={this.handleSort('name')}
                  className="offerName"
                >
                  Name
                </TH>
                <TH
                  className="hotelName"
                  sortOrder={this.getSortOrderForProp('hotel.name')}
                  onClick={this.handleSort('hotel.name')}
                >
                  Hotel
                </TH>
                <TH
                  sortOrder={this.getSortOrderForProp('hotel.countryCode')}
                  onClick={this.handleSort('hotel.countryCode')}
                  className="countryCode"
                >
                  Hotel Country Code
                </TH>
                <TH className="actions">Actions</TH>
              </TRow>
            </THead>
            <TBody tableData={this.props.offers!}>
              {(offer: IOffersListItem) => {
                return (
                  <TRow key={offer.uuid}>
                    <TD>
                      <Checkbox
                        checked={this.props.bulkActionSelectedUuids.includes(offer.uuid)}
                        onChange={() => {
                          this.toggleOfferBulkActionSelection(offer.uuid);
                        }}
                      />
                    </TD>
                    <TD title={offer.name}>{offer.name}</TD>
                    <TD title={offer.hotel.name}>{offer.hotel.name}</TD>
                    <TD title={offer.hotel.countryCode}>{offer.hotel.countryCode}</TD>
                    <TD>
                      <div className="link-button-wrapper">
                        <LinkButton className="edit-button link-button" to={`/offers/${offer.uuid}/edit`}>
                          Edit
                        </LinkButton>
                      </div>
                    </TD>
                  </TRow>
                );
              }}
            </TBody>
          </Table>
        )}

        {(this.props.offers || []).length >= 1 && (
          <Pagination
            className="pagination"
            onPageSelect={this.handlePageChange}
            pageCount={this.props.pageCount}
            currentPage={this.props.currentPage + 1}
            itemsPerPage={this.props.itemsPerPage}
            onItemsPerPageChange={this.handleItemsPerPageChange}
          />
        )}
      </OffersListStylesWrapper>
    );
  }
}

// -----------------------------------------------------------------------------
// Prop Typings
// -----------------------------------------------------------------------------
export type StateToProps = ReturnType<typeof mapStateToProps>;
export type DispatchToProps = typeof actionCreators;
export interface IOffersListProps extends StateToProps, DispatchToProps {
  className: string;
  selectedTravelAgentUuid: string;
}

const mapStateToProps = createStructuredSelector({
  requestPending: OffersListSelectors.requestPendingSelector,
  error: OffersListSelectors.errorSelector,
  offers: OffersListSelectors.offersSelector,
  filter: OffersListSelectors.filterSelector,
  totalResults: OffersListSelectors.totalResultsSelector,
  sortBy: OffersListSelectors.sortBySelector,
  sortOrder: OffersListSelectors.sortOrderSelector,
  currentPage: OffersListSelectors.currentPageSelector,
  itemsPerPage: OffersListSelectors.itemsPerPageSelector,
  pageCount: OffersListSelectors.pageCountSelector,
  isSr: isInternalUser,
  travelAgentSelectOptions: travelAgentSelectOptionsSelector,
  bulkActionSelectedUuids: OffersListSelectors.bulkActionSelectedUuidsSelector,
  isBulkDeleteConfirmOpen: OffersListSelectors.isBulkDeleteConfirmOpenSelector,
  selectedHotel: OffersListSelectors.selectedHotelSelector,
  allHotels: getBootstrapHotelsSelector,
});

const actionCreators = {
  getOffersListRequest: OffersListActions.getOffersListRequestAction,
  setFilter: OffersListActions.setFilterAction,
  setPageNumber: OffersListActions.setPageNumberAction,
  setSort: OffersListActions.setSortAction,
  setItemsPerPage: OffersListActions.setItemsPerPageAction,
  setSelectedTravelAgent: OffersListActions.setSelectedTravelAgentAction,
  getTravelAgents: getTravelAgentsRequestAction,
  addOfferUuidToBulkActionSelectedUuids: OffersListActions.addOfferUuidToBulkActionSelectedUuidsAction,
  removeOfferUuidFromBulkActionSelectedUuids: OffersListActions.removeOfferUuidFromBulkActionSelectedUuidsAction,
  toggleIsBulkDeleteConfirmOpen: OffersListActions.toggleIsBulkDeleteConfirmOpenAction,
  confirmRequestToDeleteOffers: OffersListActions.confirmRequestToDeleteOffersAction,
  dismissError: OffersListActions.dismissErrorAction,
  setSelectedHotel: OffersListActions.setSelectedHotelAction,
};

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(actionCreators, dispatch);

// -----------------------------------------------------------------------------
// Connected
// -----------------------------------------------------------------------------
const withConnect = connect<StateToProps, DispatchToProps, IOffersListProps>(mapStateToProps, mapDispatchToProps);

// @ts-ignore
export const OffersListConnected = compose(withConnect)(OffersListContainer);

export default OffersListConnected;
